import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Bike, PackageCheck } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import type {
  SalesDocumentDetail,
  SalesDocumentSummary,
} from '@aeris/contracts'
import {
  formatMoney,
  isPositive,
  MONEY_SCALE,
  parseHumanAmount,
  toString,
  ZERO_MONEY,
} from '@aeris/money'
import { formatDocumentNumber } from '@aeris/printing'
import {
  Button,
  ConfirmDialog,
  InlineAlert,
  Pagination,
  RowActions,
  Section,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  RowOpener,
  TableRow,
  VisuallyHidden,
} from '@aeris/ui'

import { fetchCustomerCredit } from '../lib/api/exchanges.ts'
import {
  ApiError,
  dispatchSalesDocument,
  fetchCredit,
  fetchPaymentMethods,
  fetchSalesDocument,
  fetchSalesDocuments,
  pickupSalesDocument,
  transitionSalesDocument,
} from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'
import { customerLookup } from '../lib/customer-lookup.ts'

import { AuthorisationDialog } from './authorisation-dialog.tsx'
import { CourierDialog } from './courier-dialog.tsx'
import { PosPaymentDialog, type PosTender } from './pos-payment-dialog.tsx'

/**
 * Esperando retirada — F218.
 *
 * O dono: *"os clientes estão pagando e indo buscar depois na loja"*. O que
 * foi pago pelo "Pagar e retirar depois" do orçamento vira um pedido
 * confirmado: a mercadoria está separada, o dinheiro já entrou, e falta só o
 * cliente aparecer. É ele aparecer que marca a hora da retirada e tira do
 * estoque — o Retirou desta lista, pela retirada de pedido (F159).
 *
 * Um pedido confirmado que ainda deve alguma coisa (o do vendedor, com ou sem
 * sinal) também está esperando o cliente, e aparece aqui: o Retirou cobra o
 * que falta.
 */

const PAGE_SIZE = 10

const money = (wire: string) =>
  parseHumanAmount(wire.replace('.', ','), MONEY_SCALE) ?? ZERO_MONEY

/** `05/08 14:32`, como na lista de cima. */
const stamp = (iso: string): string => {
  const at = new Date(iso)
  const pad = (value: number): string => String(value).padStart(2, '0')
  return `${pad(at.getDate())}/${pad(at.getMonth() + 1)} ${pad(at.getHours())}:${pad(at.getMinutes())}`
}

const clock = (iso: string | null): string => {
  if (iso === null) return ''
  const at = new Date(iso)
  return ` às ${String(at.getHours()).padStart(2, '0')}:${String(at.getMinutes()).padStart(2, '0')}`
}

export const AwaitingPickup = ({
  cashSessionId,
  onDone,
  onOpen,
}: {
  /** O caixa aberto deste terminal; nulo desabilita o Retirou. */
  cashSessionId: string | null
  onDone: (message: string) => void
  /**
   * Abrir o pedido (a ficha, com o editar): quem atende precisa conferir e
   * corrigir o pedido antes de entregar, e a lista não abria nada.
   */
  onOpen?: (documentId: string) => void
}): ReactNode => {
  const { allows, session } = useAuth()
  // O balcão desta filial: o pedido de outra loja não é retirado aqui.
  const branchId = session?.context?.branchId ?? ''
  const canTakeMoney = allows('pos.operate') && allows('payment.write')
  /*
   * Cancelar daqui (24/10): o pedido do site que o cliente desistiu não
   * tinha botão. Sem `sales.cancel`, o servidor pede a senha do supervisor.
   */
  const canCancel = canTakeMoney || allows('sales.cancel')
  const showActions = canTakeMoney || canCancel
  const queryClient = useQueryClient()
  const [page, setPage] = useState(1)
  const [error, setError] = useState<string | null>(null)
  /** Já pago: só falta confirmar que o cliente levou. */
  const [confirming, setConfirming] = useState<SalesDocumentDetail | null>(null)
  /** Ainda deve: a cobrança do que falta. */
  const [paying, setPaying] = useState<SalesDocumentDetail | null>(null)
  const [delivering, setDelivering] = useState<SalesDocumentSummary | null>(
    null
  )

  const orders = useQuery({
    queryKey: ['sales-documents', 'awaiting-pickup', branchId, page],
    queryFn: ({ signal }) =>
      fetchSalesDocuments(
        {
          ...(branchId === '' ? {} : { branchId }),
          kind: 'order',
          status: 'confirmed',
          // O pedido da fila da rota (F266) espera o motoboy, não o cliente.
          hideRouteQueue: true,
          // Quem espera há mais tempo primeiro.
          sort: 'issued_at',
          direction: 'asc',
          page,
          pageSize: PAGE_SIZE,
        },
        signal
      ),
    placeholderData: (previous) => previous,
  })

  const methods = useQuery({
    queryKey: ['payment-methods', { status: 'active' }],
    queryFn: ({ signal }) => fetchPaymentMethods({ status: 'active' }, signal),
    enabled: paying !== null,
  })

  const credit = useQuery({
    queryKey: ['credit', paying?.customerPartyId],
    queryFn: ({ signal }) => fetchCredit(paying?.customerPartyId ?? '', signal),
    enabled: paying !== null,
  })

  const voucher = useQuery({
    queryKey: ['customer-credit', paying?.customerPartyId],
    queryFn: ({ signal }) =>
      fetchCustomerCredit(paying?.customerPartyId ?? '', signal),
    enabled: paying !== null && allows('exchange.read'),
  })

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['sales-documents'] })
    void queryClient.invalidateQueries({ queryKey: ['stock'] })
    void queryClient.invalidateQueries({ queryKey: ['cash-session'] })
    void queryClient.invalidateQueries({ queryKey: ['finance'] })
  }

  const fail = (failure: unknown, fallback: string): void => {
    setError(failure instanceof ApiError ? failure.message : fallback)
  }

  /** Abre o pedido para saber quanto ainda falta. */
  const open = useMutation({
    mutationFn: async (order: SalesDocumentSummary) =>
      await fetchSalesDocument(order.id),
    onSuccess: (response) => {
      setError(null)
      const detail = response.document
      if (isPositive(money(detail.outstandingAmount))) setPaying(detail)
      else setConfirming(detail)
    },
    onError: (failure: unknown) => {
      fail(failure, 'Não foi possível abrir o pedido.')
    },
  })

  const pickup = useMutation({
    mutationFn: async (input: {
      order: SalesDocumentDetail
      tenders: readonly PosTender[]
      creditAuthorisationId?: string | null
    }) =>
      await pickupSalesDocument(input.order.id, {
        version: input.order.version,
        cashSessionId,
        payments: input.tenders.map((tender) => ({
          paymentMethodId: tender.methodId,
          amount: toString(tender.amount),
        })),
        creditAuthorisationId: input.creditAuthorisationId ?? null,
      }),
    onSuccess: (response) => {
      setError(null)
      setConfirming(null)
      setPaying(null)
      onDone(
        `Pedido nº ${formatDocumentNumber(response.document.number, '')} retirado${clock(response.document.dispatchedAt)}. A mercadoria saiu do estoque.`
      )
      refresh()
    },
    onError: (failure: unknown) => {
      fail(failure, 'Não foi possível registrar a retirada.')
    },
  })

  const deliver = useMutation({
    mutationFn: async (input: {
      order: SalesDocumentSummary
      courierPartyId: string
    }) =>
      await dispatchSalesDocument(input.order.id, {
        version: input.order.version,
        courierPartyId: input.courierPartyId,
      }),
    onSuccess: (response) => {
      setError(null)
      setDelivering(null)
      onDone(
        `Pedido nº ${formatDocumentNumber(response.document.number, '')} despachado com o motoboy.`
      )
      refresh()
    },
    onError: (failure: unknown) => {
      fail(failure, 'Não foi possível despachar a entrega.')
    },
  })

  const [cancelling, setCancelling] = useState<SalesDocumentSummary | null>(
    null
  )
  const [authorising, setAuthorising] = useState<SalesDocumentSummary | null>(
    null
  )
  const cancel = useMutation({
    mutationFn: async (input: {
      order: SalesDocumentSummary
      authorisationId: string | null
    }) => {
      const { document } = await fetchSalesDocument(input.order.id)
      return await transitionSalesDocument(input.order.id, {
        version: document.version,
        to: 'cancelled',
        reason: `Pedido nº ${formatDocumentNumber(input.order.number, '')} cancelado na retirada`,
        authorisationId: input.authorisationId,
      })
    },
    onSuccess: (_response, input) => {
      setError(null)
      setCancelling(null)
      onDone(
        `Pedido nº ${formatDocumentNumber(input.order.number, '')} cancelado. A mercadoria voltou ao estoque.`
      )
      refresh()
    },
    onError: (failure: unknown, input) => {
      setCancelling(null)
      if (
        failure instanceof ApiError &&
        failure.code === 'authorisation_required' &&
        input.authorisationId === null
      ) {
        setError(null)
        setAuthorising(input.order)
        return
      }
      fail(
        failure,
        'Não foi possível cancelar. Tente de novo, ou cancele em Vendas.'
      )
    },
  })

  const items = orders.data?.items ?? []
  const total = orders.data?.total ?? 0

  // Nada esperando: a seção não ocupa a tela do caixa.
  if (total === 0 && page === 1) return null

  return (
    <Section
      title="Esperando retirada"
      description="Pedidos que o cliente vem buscar. A mercadoria está separada; o Retirou marca a hora em que ele levou e tira do estoque."
    >
      {error !== null && <InlineAlert tone="danger">{error}</InlineAlert>}

      {canTakeMoney && cashSessionId === null && (
        <InlineAlert tone="warning">
          Abra o caixa para registrar uma retirada.
        </InlineAlert>
      )}

      <TableContainer>
        <Table aria-label="Esperando retirada">
          <TableHead>
            <TableRow>
              <TableHeaderCell className="w-20">Nº</TableHeaderCell>
              <TableHeaderCell>Cliente</TableHeaderCell>
              <TableHeaderCell>Vendedor</TableHeaderCell>
              <TableHeaderCell className="w-28">Desde</TableHeaderCell>
              <TableHeaderCell numeric className="w-28">
                Total
              </TableHeaderCell>
              {showActions ? (
                <TableHeaderCell>
                  <VisuallyHidden>Ações</VisuallyHidden>
                </TableHeaderCell>
              ) : null}
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((order) => (
              <TableRow
                key={order.id}
                {...(onOpen === undefined
                  ? {}
                  : {
                      onOpen: () => {
                        onOpen(order.id)
                      },
                    })}
              >
                <TableCell className="font-(--font-mono) font-(--weight-semibold) text-(--text-primary)">
                  {onOpen === undefined ? (
                    formatDocumentNumber(order.number)
                  ) : (
                    <RowOpener
                      onOpen={() => {
                        onOpen(order.id)
                      }}
                    >
                      {formatDocumentNumber(order.number)}
                    </RowOpener>
                  )}
                </TableCell>
                <TableCell>{order.customerName}</TableCell>
                <TableCell className="text-(--text-secondary)">
                  {order.salespersonName ?? '—'}
                </TableCell>
                <TableCell className="text-(length:--text-xs) text-(--text-muted)">
                  {stamp(order.issuedAt)}
                </TableCell>
                <TableCell numeric>
                  {formatMoney(money(order.totalAmount))}
                </TableCell>
                {showActions ? (
                  <TableCell>
                    <RowActions
                      name={`pedido nº ${formatDocumentNumber(order.number)}`}
                      primary={
                        <>
                          {canTakeMoney ? (
                            <>
                              <Button
                                variant="primary"
                                size="sm"
                                disabled={
                                  open.isPending || cashSessionId === null
                                }
                                leadingIcon={
                                  <PackageCheck
                                    aria-hidden="true"
                                    className="h-3.5 w-3.5"
                                  />
                                }
                                onClick={() => {
                                  open.mutate(order)
                                }}
                              >
                                Retirou
                              </Button>
                              <Button
                                variant="secondary"
                                size="sm"
                                leadingIcon={
                                  <Bike
                                    aria-hidden="true"
                                    className="h-3.5 w-3.5"
                                  />
                                }
                                onClick={() => {
                                  setDelivering(order)
                                }}
                              >
                                Entregar
                              </Button>
                            </>
                          ) : null}
                          {canCancel ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setCancelling(order)
                              }}
                            >
                              Cancelar
                            </Button>
                          ) : null}
                        </>
                      }
                    />
                  </TableCell>
                ) : null}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Pagination
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        onPageChange={setPage}
      />

      <ConfirmDialog
        open={cancelling !== null}
        onOpenChange={(next) => {
          if (!next) setCancelling(null)
        }}
        title={`Cancelar o pedido nº ${formatDocumentNumber(cancelling?.number ?? null, '')}?`}
        description={`${cancelling?.customerName ?? ''}: o pedido é cancelado e a mercadoria separada volta ao estoque. Se o cliente já pagou, o sistema pergunta se devolve o dinheiro ou vira vale.`}
        confirmLabel="Cancelar pedido"
        cancelLabel="Voltar"
        tone="danger"
        loading={cancel.isPending}
        onConfirm={() => {
          if (cancelling !== null) {
            cancel.mutate({ order: cancelling, authorisationId: null })
          }
        }}
      />

      {authorising === null ? null : (
        <AuthorisationDialog
          title={`Cancelar o pedido nº ${formatDocumentNumber(authorising.number, '')}`}
          description="O pedido está segurando mercadoria. Ela volta ao estoque, e um supervisor precisa autorizar."
          action="sales.cancel"
          targetType="sales_document"
          targetId={authorising.id}
          onAuthorised={(authorisationId) => {
            const order = authorising
            setAuthorising(null)
            cancel.mutate({ order, authorisationId })
          }}
          onClose={() => {
            setAuthorising(null)
          }}
        />
      )}

      <ConfirmDialog
        open={confirming !== null}
        onOpenChange={(next) => {
          if (!next) setConfirming(null)
        }}
        title={`O cliente levou o pedido nº ${formatDocumentNumber(confirming?.number ?? null, '')}?`}
        description="Já está pago. A mercadoria sai do estoque agora, e esta é a hora que fica como retirada."
        confirmLabel="Retirou"
        cancelLabel="Voltar"
        loading={pickup.isPending}
        onConfirm={() => {
          if (confirming !== null) {
            pickup.mutate({ order: confirming, tenders: [] })
          }
        }}
      />

      {paying !== null && (
        <PosPaymentDialog
          total={money(paying.outstandingAmount)}
          methods={methods.data?.methods ?? []}
          credit={credit.data ?? null}
          voucherBalance={voucher.data?.balance ?? null}
          voucherLookup={customerLookup(voucher, {
            hasCustomer: true,
            allowed: allows('exchange.read'),
          })}
          creditLookup={customerLookup(credit, {
            hasCustomer: true,
            allowed: true,
          })}
          pending={pickup.isPending}
          error={error}
          onConfirm={(tenders, creditAuthorisationId) => {
            pickup.mutate({ order: paying, tenders, creditAuthorisationId })
          }}
          onClose={() => {
            setPaying(null)
          }}
        />
      )}

      {delivering !== null && (
        <CourierDialog
          title={`Entregar o pedido nº ${formatDocumentNumber(delivering.number, '')}`}
          pending={deliver.isPending}
          error={error}
          onChoose={(courierPartyId) => {
            deliver.mutate({ order: delivering, courierPartyId })
          }}
          onClose={() => {
            setDelivering(null)
          }}
        />
      )}
    </Section>
  )
}
